import moment from 'moment';
import { UseFormWatch } from 'react-hook-form';
import {
  Chip,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  BrokenImage as BrokenImageIcon,
  DescriptionRounded as DescriptionIcon,
  EventAvailableRounded as EventAvailableIcon,
  EventRounded as EventIcon,
  RoomRounded as RoomIcon,
  ScheduleRounded as ScheduleIcon,
} from '@material-ui/icons';

import { BASKET_IMAGE_PATH } from '../../../baskets/constants/baskets-app-paths.constants';
import { Organization } from '../../../organizations/types/organization.type';
import { HeaderSummary, HeaderSummaryItem } from '../../../shared/components/HeaderSummary.component';
import { PRODUCT_IMAGE_PATH } from '../../../products/constants/products-app-paths.constants';
import { QuoteItem } from '../../types/quote.type';
import { computeTotalsLocal } from '../../utils/compute-totals-local.util';
import { QuoteItemTypeChip } from '../Chips/QuoteItemTypeChip.component';
import { CreateQuoteFormValues } from './CreateQuoteStepper.component';

type Props = {
  organization: Organization;
  watch: UseFormWatch<CreateQuoteFormValues>;
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    '& > h4': {
      margin: 0,
      opacity: 0.7,
    },
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  countChip: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    borderRadius: 4,
    fontWeight: 600,
    height: 20,
  },
  conditionsEllipsis: {
    display: 'inline-block',
    maxWidth: 200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    verticalAlign: 'bottom',
  },
  totals: {
    display: 'grid',
    gridTemplateColumns: 'max-content max-content',
    columnGap: '2rem',
    rowGap: '0.25rem',
    justifyContent: 'end',
    padding: '0.5rem 0.75rem',
  },
  totalsValue: {
    textAlign: 'right' as const,
  },
  bold: { fontWeight: 600 },
  photo: { width: 40, height: 40, objectFit: 'cover' as const, borderRadius: 4 },
  photoFallback: { width: 40, height: 40, color: theme.palette.grey[400] },
}));

function formatLongDate(value: string | undefined): string {
  if (!value) return '—';
  return moment(value).format('dddd D MMMM YYYY');
}

function resolvePhotoUrl(item: QuoteItem): string | undefined {
  if (!item.photo) return undefined;
  return item.type === 'product' ? PRODUCT_IMAGE_PATH(item.photo) : BASKET_IMAGE_PATH(item.photo);
}

export function SummaryStep({ organization, watch }: Props) {
  const classes = useStyles();

  const items = watch('items') ?? [];
  const globalDiscount = watch('globalDiscount');
  const delivery = watch('delivery');
  const validUntil = watch('validUntil');
  const conditions = watch('conditions') ?? '';

  const sanitizedItems = items.map((item) => ({
    price: Number(item.price) || 0,
    quantity: Number(item.quantity) || 0,
    tvaRate: item.tvaRate,
  }));

  const totals = computeTotalsLocal({
    items: sanitizedItems,
    globalDiscount,
  });

  const hasDiscount = Boolean(globalDiscount && globalDiscount.value > 0);
  const discountAmount = totals.totalDiscount;

  let discountLine = '';
  if (hasDiscount && globalDiscount) {
    if (globalDiscount.reduction === 'percent') {
      discountLine = `${globalDiscount.value}% (${discountAmount.toFixed(2)} €)`;
    } else {
      discountLine = `${globalDiscount.value.toFixed(2)} €`;
    }
  }

  const headerItems: HeaderSummaryItem[] = [
    {
      key: 'deliveryDate',
      label: 'Date de livraison',
      value: formatLongDate(delivery?.date),
      Icon: <EventIcon color="action" />,
    },
    {
      key: 'deliverySchedule',
      label: 'Horaire de livraison',
      value: delivery?.schedule || '—',
      Icon: <ScheduleIcon color="action" />,
    },
    {
      key: 'deliveryPoint',
      label: 'Point de livraison',
      value:
        organization.deliveryPoints.find((deliveryPoint) => deliveryPoint.id === delivery?.deliveryPointId)?.name ||
        '—',
      Icon: <RoomIcon color="action" />,
    },
    {
      key: 'validUntil',
      label: 'Valable jusqu\'au',
      value: formatLongDate(validUntil),
      Icon: <EventAvailableIcon color="action" />,
    },
  ];

  if (conditions.trim().length > 0) {
    headerItems.push({
      key: 'conditions',
      label: 'Conditions',
      value: (
        <Tooltip title={conditions}>
          <span className={classes.conditionsEllipsis}>{conditions}</span>
        </Tooltip>
      ),
      Icon: <DescriptionIcon color="action" />,
    });
  }

  return (
    <div className={classes.root}>
      <HeaderSummary items={headerItems} />

      <section className={classes.section}>
        <div className={classes.sectionTitle}>
          <h4>Produits</h4>
          <Chip size="small" className={classes.countChip} label={items.length} />
        </div>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ width: 56 }} />
                <TableCell style={{ width: 120 }} />
                <TableCell style={{ fontWeight: 'bold' }}>Nom</TableCell>
                <TableCell style={{ fontWeight: 'bold' }}>Référence</TableCell>
                <TableCell style={{ fontWeight: 'bold' }} align="right">
                  Quantité
                </TableCell>
                <TableCell style={{ fontWeight: 'bold' }} align="right">
                  Prix unitaire HT
                </TableCell>
                <TableCell style={{ fontWeight: 'bold' }} align="right">
                  Total HT
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Aucun produit
                  </TableCell>
                </TableRow>
              )}
              {items.map((item, index) => {
                const quantity = Number(item.quantity) || 0;
                const price = Number(item.price) || 0;
                const lineTotal = quantity * price;
                const itemKey = item.type === 'product' ? item.productId : item.basketId;
                const photoUrl = resolvePhotoUrl(item);
                return (
                  <TableRow key={`${itemKey}-${index}`}>
                    <TableCell>
                      {photoUrl ? (
                        <img src={photoUrl} alt="" className={classes.photo} />
                      ) : (
                        <BrokenImageIcon className={classes.photoFallback} />
                      )}
                    </TableCell>
                    <TableCell>
                      <QuoteItemTypeChip type={item.type} />
                    </TableCell>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>{item.ref}</TableCell>
                    <TableCell align="right">{quantity}</TableCell>
                    <TableCell align="right">{price.toFixed(2)} €</TableCell>
                    <TableCell align="right">{lineTotal.toFixed(2)} €</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <div className={classes.totals}>
          {hasDiscount && (
            <>
              <Typography>Remise globale</Typography>
              <Typography className={classes.totalsValue}>{discountLine}</Typography>
            </>
          )}
          <Typography>Sous-total HT</Typography>
          <Typography className={classes.totalsValue}>{totals.totalHt.toFixed(2)} €</Typography>
          <Typography className={classes.bold}>Total TTC</Typography>
          <Typography className={`${classes.totalsValue} ${classes.bold}`}>
            {totals.totalTtc.toFixed(2)} €
          </Typography>
        </div>
      </section>
    </div>
  );
}
